import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2, Cloud, Download, HardDrive, History, Info, Loader2, Plug, Plus, RefreshCw,
  ShieldAlert, Terminal, Trash2, XCircle, Pencil, Cpu, KeyRound, Mail,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export default function CloudSyncPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const { data: config } = useQuery({ queryKey: ["cs-config"], queryFn: api.cloudSync.getConfig, enabled: isAdmin });
  const { data: accounts } = useQuery({ queryKey: ["cs-accounts"], queryFn: api.cloudSync.listAccounts, enabled: isAdmin });
  const { data: mappings } = useQuery({ queryKey: ["cs-mappings"], queryFn: api.cloudSync.listMappings, enabled: isAdmin });
  const { data: logs } = useQuery({ queryKey: ["cs-logs"], queryFn: () => api.cloudSync.listLogs(15), enabled: isAdmin, refetchInterval: 20000 });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: () => api.cameras.list(), enabled: isAdmin });

  const [nasForm, setNasForm] = useState({});
  useEffect(() => { if (config) setNasForm({ nasQuickConnectId: config.nasQuickConnectId || "", nasUsername: config.nasUsername || "", nasBasePath: config.nasBasePath || "", nasPassword: "", syncIntervalMinutes: config.syncIntervalMinutes ?? 10, lookbackHours: config.lookbackHours ?? 24 }); }, [config]);

  const saveCfg = useMutation({ mutationFn: api.cloudSync.updateConfig, onSuccess: () => { toast.success("Configurație salvată"); qc.invalidateQueries({ queryKey: ["cs-config"] }); }, onError: (e) => toast.error(apiError(e)) });
  const [nasResult, setNasResult] = useState(null);
  const testNas = useMutation({
    mutationFn: () => api.cloudSync.testNas({ quickConnectId: nasForm.nasQuickConnectId, username: nasForm.nasUsername, password: nasForm.nasPassword || config?.nasPassword }),
    onSuccess: (r) => { setNasResult(r); r.success ? toast.success(`NAS conectat: ${r.shares?.length || 0} foldere`) : toast.error(`NAS: ${r.error}`); },
    onError: (e) => toast.error(apiError(e)),
  });
  const enableAuto = useMutation({ mutationFn: () => api.cloudSync.enableAuto({ intervalMinutes: nasForm.syncIntervalMinutes || 10 }), onSuccess: () => { toast.success("Auto-sync activat (serverul va sincroniza la interval)"); qc.invalidateQueries({ queryKey: ["cs-config"] }); }, onError: (e) => toast.error(apiError(e)) });
  const disableAuto = useMutation({ mutationFn: api.cloudSync.disableAuto, onSuccess: () => { toast.success("Auto-sync dezactivat"); qc.invalidateQueries({ queryKey: ["cs-config"] }); }, onError: (e) => toast.error(apiError(e)) });

  const delMapping = useMutation({ mutationFn: (id) => api.cloudSync.deleteMapping(id), onSuccess: () => { toast.success("Mapare ștearsă"); qc.invalidateQueries({ queryKey: ["cs-mappings"] }); }, onError: (e) => toast.error(apiError(e)) });
  const delAccount = useMutation({ mutationFn: (id) => api.cloudSync.deleteAccount(id), onSuccess: () => { toast.success("Cont șters"); qc.invalidateQueries({ queryKey: ["cs-accounts"] }); }, onError: (e) => toast.error(apiError(e)) });
  const toggleMapping = useMutation({ mutationFn: ({ id, syncEnabled }) => api.cloudSync.updateMapping(id, { syncEnabled }), onSuccess: () => qc.invalidateQueries({ queryKey: ["cs-mappings"] }), onError: (e) => toast.error(apiError(e)) });
  const backfillCamera = useMutation({ mutationFn: ({ cameraId }) => api.cloudSync.backfillCamera({ cameraId, days: 30 }), onSuccess: () => { toast.success("Recuperare pornită (30 zile) — serverul aduce filmele acestei camere în câteva momente"); qc.invalidateQueries({ queryKey: ["cs-config"] }); qc.invalidateQueries({ queryKey: ["cs-logs"] }); }, onError: (e) => toast.error(apiError(e)) });
  const cancelBackfill = useMutation({ mutationFn: api.cloudSync.backfillCancel, onSuccess: () => { toast.success("Recuperare anulată"); qc.invalidateQueries({ queryKey: ["cs-config"] }); }, onError: (e) => toast.error(apiError(e)) });
  const autoMap = useMutation({
    mutationFn: api.cloudSync.autoMap,
    onSuccess: (r) => {
      const n = r.count || 0;
      toast.success(n ? `${n} camere mapate automat (${[...r.updated, ...r.added].slice(0, 6).join(", ")}${n > 6 ? "…" : ""})` : "Nimic de actualizat — toate camerele sunt deja mapate corect");
      if (r.errors && Object.keys(r.errors).length) toast.error(`Erori: ${Object.entries(r.errors).map(([k, v]) => `${k}: ${v}`).join("; ")}`);
      qc.invalidateQueries({ queryKey: ["cs-mappings"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const repairCameras = useMutation({
    mutationFn: api.cloudSync.repairCameras,
    onSuccess: (r) => {
      toast.success(r.count ? `Restaurare completă: ${r.createdCameras.length} camere, ${r.createdProjects.length} șantiere, ${r.mappingsFixed.length} mapări` : "Totul era deja la locul lui — nimic de restaurat");
      qc.invalidateQueries({ queryKey: ["cs-mappings"] });
      qc.invalidateQueries({ queryKey: ["cameras"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const triggerSync = useMutation({
    mutationFn: api.cloudSync.trigger,
    onSuccess: (r) => { toast.success(r.busy ? "O sincronizare este deja în curs" : "Sincronizare pornită pe server — clipurile apar în câteva momente"); qc.invalidateQueries({ queryKey: ["cs-logs"] }); qc.invalidateQueries({ queryKey: ["cs-config"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  if (!isAdmin) {
    return <DashboardLayout><div className="max-w-2xl mx-auto mt-12"><Card><CardContent className="flex flex-col items-center gap-3 py-12 text-center"><ShieldAlert className="h-10 w-10 text-muted-foreground" /><p className="text-lg font-medium">Acces restricționat</p><p className="text-sm text-muted-foreground">Doar administratorii pot configura Cloud Sync.</p></CardContent></Card></div></DashboardLayout>;
  }

  const cameraName = (id) => cameras?.find((c) => c.id === id)?.name ?? `Cameră #${id}`;
  const accountLabel = (id) => accounts?.find((a) => a.id === id)?.email ?? `Cont #${id}`;
  const lastLog = logs?.[0];

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6 max-w-5xl" data-testid="cloudsync-page">
        <header>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight flex items-center gap-2"><Cloud className="h-6 w-6 text-primary" /> Cloud Sync (Reolink → HGS → NAS)</h1>
          <p className="text-sm text-muted-foreground mt-1">Aducere automată a clipurilor din conturile Reolink, direct pe server — fără agent, fără instalări.</p>
        </header>

        <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900 flex gap-3">
          <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="font-medium">Autentificare cu email + parolă (fără cookie-uri)</p>
            <p>Adaugi contul Reolink cu <strong>email și parolă</strong>, apoi apeși <strong>Autentifică</strong>. Reolink îți trimite un <strong>cod de verificare pe email</strong> (2FA) pe care îl introduci aici o singură dată. După aceea aplicația se reautentifică singură și rămâne conectată ~<strong>30 de zile</strong>. Când expiră, primești din nou un cod pe email — îl reintroduci și gata.</p>
          </div>
        </div>

        {/* SINCRONIZARE AUTOMATĂ (SERVER) */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Sincronizare automată</CardTitle><CardDescription>Rulează pe server. Pornește o sincronizare acum sau lasă auto-sync să aducă clipurile la interval.</CardDescription></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button onClick={() => triggerSync.mutate()} disabled={triggerSync.isPending} data-testid="sync-now-btn">{triggerSync.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}Sincronizează acum</Button>
              {config?.syncEnabled ? (
                <Button variant="outline" className="bg-background" onClick={() => disableAuto.mutate()} data-testid="auto-off-btn"><XCircle className="h-4 w-4 mr-2" />Oprește auto-sync</Button>
              ) : (
                <Button variant="outline" className="bg-background" onClick={() => enableAuto.mutate()} data-testid="auto-on-btn"><RefreshCw className="h-4 w-4 mr-2" />Pornește auto-sync</Button>
              )}
              <Badge variant={config?.syncEnabled ? "default" : "secondary"}>{config?.syncEnabled ? `Auto ON · la fiecare ${config?.syncIntervalMinutes || 10} min` : "Auto OFF"}</Badge>
              {lastLog && <span className="text-xs text-muted-foreground">Ultima sincronizare: {new Date(lastLog.startedAt).toLocaleString("ro-RO")} ({lastLog.status})</span>}
              {!lastLog && <span className="text-xs text-muted-foreground">Încă nu s-a sincronizat.</span>}
            </div>
            {config?.lastSyncError && <p className="text-xs text-destructive">Ultima eroare: {config.lastSyncError}</p>}
            <p className="text-xs text-muted-foreground">Alternativ, poți încărca manual arhive din pagina <strong>Încărcare arhive</strong>.</p>
          </CardContent>
        </Card>

        {/* CONTURI REOLINK */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div><CardTitle className="text-base flex items-center gap-2"><Cloud className="h-4 w-4" /> Conturi Reolink</CardTitle><CardDescription>Email + parolă per cont. Apasă «Autentifică» și introdu codul primit pe email (valabil ~30 zile).</CardDescription></div>
            <AccountDialog onSaved={() => qc.invalidateQueries({ queryKey: ["cs-accounts"] })} />
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {!accounts?.length ? <p className="text-sm text-muted-foreground py-4">Niciun cont Reolink. Adăugați cloud1 și cloud2 cu email + parolă.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Cont</TableHead><TableHead>User ID</TableHead><TableHead>Stare</TableHead><TableHead>Camere</TableHead><TableHead>Activ</TableHead><TableHead className="text-right">Acțiuni</TableHead></TableRow></TableHeader>
                <TableBody>
                  {accounts.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell className="font-medium">{a.email}</TableCell>
                      <TableCell className="font-mono text-xs">{a.userId || "—"}</TableCell>
                      <TableCell>
                        {a.sessionValid === true ? <Badge className="text-[10px] bg-emerald-600 hover:bg-emerald-600">autentificat</Badge>
                          : a.sessionValid === false ? <Badge variant="destructive" className="text-[10px]" title={a.lastError || ""}>expirat</Badge>
                          : a.mfaPending ? <Badge variant="outline" className="text-[10px]">așteaptă cod</Badge>
                          : a.authenticated ? <Badge variant="secondary" className="text-[10px]">salvat</Badge>
                          : <Badge variant="destructive" className="text-[10px]">neautentificat</Badge>}
                        {a.trustExpiresAt && a.sessionValid !== false && <div className="text-[10px] text-muted-foreground mt-0.5">valabil până {new Date(a.trustExpiresAt).toLocaleDateString("ro-RO")}</div>}
                      </TableCell>
                      <TableCell>{a.cameraCount}</TableCell>
                      <TableCell><Badge variant={a.syncEnabled ? "default" : "secondary"}>{a.syncEnabled ? "DA" : "NU"}</Badge></TableCell>
                      <TableCell className="text-right flex justify-end gap-1">
                        <LoginDialog account={a} onDone={() => qc.invalidateQueries({ queryKey: ["cs-accounts"] })} />
                        <AccountDialog account={a} onSaved={() => qc.invalidateQueries({ queryKey: ["cs-accounts"] })} />
                        <Button size="sm" variant="outline" className="bg-background text-destructive hover:text-destructive" onClick={() => { if (confirm(`Ștergi contul ${a.email}?`)) delAccount.mutate(a.id); }} data-testid={`delete-account-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="rounded-md border bg-muted/30 p-3 text-xs mt-3 flex gap-2 text-muted-foreground" data-testid="auth-guide">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <p>Codul 2FA ajunge pe adresa contului (ex. <code>cloud1@highbergsolution.ro</code>) în câteva secunde. Dacă ai 2FA prin aplicație de autentificare, folosește oricum opțiunea pe email — aplicația cere cod pe email. Odată autentificat, sesiunea se reînnoiește automat până la ~30 de zile.</p>
            </div>
          </CardContent>
        </Card>

        {/* NAS */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><HardDrive className="h-4 w-4" /> NAS Synology (opțional)</CardTitle><CardDescription>Backup automat pe NAS: fiecare clip nou adus de pe Reolink e salvat și pe NAS. Testează conexiunea mai jos.</CardDescription></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>QuickConnect ID</Label><Input value={nasForm.nasQuickConnectId || ""} onChange={(e) => setNasForm((f) => ({ ...f, nasQuickConnectId: e.target.value }))} placeholder="ex: HIGHBERGNAS" /></div>
            <div className="grid gap-1.5"><Label>Folder de bază</Label><Input value={nasForm.nasBasePath || ""} onChange={(e) => setNasForm((f) => ({ ...f, nasBasePath: e.target.value }))} placeholder="/Camere" /></div>
            <div className="grid gap-1.5"><Label>Utilizator</Label><Input value={nasForm.nasUsername || ""} onChange={(e) => setNasForm((f) => ({ ...f, nasUsername: e.target.value }))} /></div>
            <div className="grid gap-1.5"><Label>Parolă {config?.nasQuickConnectId && <span className="text-xs text-muted-foreground">(gol = păstrează parola)</span>}</Label><Input type="password" value={nasForm.nasPassword || ""} onChange={(e) => setNasForm((f) => ({ ...f, nasPassword: e.target.value }))} /></div>
            <div className="grid gap-1.5"><Label>Interval auto-sync (minute)</Label><Input type="number" value={nasForm.syncIntervalMinutes ?? 10} onChange={(e) => setNasForm((f) => ({ ...f, syncIntervalMinutes: Number(e.target.value) || 10 }))} /></div>
            <div className="flex items-end"><Button variant="outline" className="bg-background" onClick={() => testNas.mutate()} disabled={testNas.isPending || !nasForm.nasQuickConnectId}>{testNas.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plug className="h-4 w-4 mr-2" />}Testează NAS</Button></div>
            {nasResult && (
              <div className={`md:col-span-2 rounded-md border p-3 text-sm ${nasResult.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                {nasResult.success ? <p className="flex items-center gap-2 text-green-700"><CheckCircle2 className="h-4 w-4" /> Conectat la {nasResult.resolvedUrl}. Foldere: {nasResult.shares?.map((s) => s.name).join(", ") || "—"}</p> : <p className="flex items-center gap-2 text-red-700"><XCircle className="h-4 w-4" /> {nasResult.error}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* RECUPERARE FILME VECHI DIN CLOUD */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Recuperare filme vechi din cloud</CardTitle><CardDescription>Reolink păstrează în cloud maxim 30 de zile. Mărește fereastra de căutare ca serverul să aducă și clipurile mai vechi (ex. cele de la Camera11). După recuperare, revino la 24h pentru funcționarea normală.</CardDescription></CardHeader>
          <CardContent className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="grid gap-1.5 flex-1">
              <Label>Caută în cloud (ultimele ore)</Label>
              <Input type="number" min={1} max={720} value={nasForm.lookbackHours ?? 24} onChange={(e) => setNasForm((f) => ({ ...f, lookbackHours: Number(e.target.value) || 24 }))} data-testid="lookback-hours-input" />
              <span className="text-xs text-muted-foreground">Actual: {config?.lookbackHours ?? 24}h {config?.lookbackHours >= 48 ? `(≈${Math.round((config?.lookbackHours || 0) / 24)} zile)` : ""}</span>
            </div>
            <Button variant="outline" className="bg-background" onClick={() => saveCfg.mutate({ lookbackHours: nasForm.lookbackHours || 24 })} disabled={saveCfg.isPending} data-testid="save-lookback-btn">Salvează</Button>
            <Button onClick={() => { setNasForm((f) => ({ ...f, lookbackHours: 720 })); saveCfg.mutate({ lookbackHours: 720 }, { onSuccess: () => triggerSync.mutate() }); toast.info("Fereastră setată la 30 zile — pornesc sincronizarea acum"); }} disabled={saveCfg.isPending} data-testid="backfill-30d-btn"><History className="h-4 w-4 mr-2" />Recuperează 30 zile</Button>
          </CardContent>
        </Card>

        <div>
          <Button onClick={() => saveCfg.mutate({ nasQuickConnectId: nasForm.nasQuickConnectId, nasUsername: nasForm.nasUsername, nasBasePath: nasForm.nasBasePath, syncIntervalMinutes: nasForm.syncIntervalMinutes || 10, lookbackHours: nasForm.lookbackHours || 24, ...(nasForm.nasPassword ? { nasPassword: nasForm.nasPassword } : {}) })} disabled={saveCfg.isPending} data-testid="save-cs-config-btn">Salvează NAS & interval</Button>
        </div>

        {/* BACKFILL PENDING BANNER */}
        {config?.backfillCameraId && (
          <div className="rounded-lg border border-sky-300 bg-sky-50 p-4 text-sm text-sky-900 flex items-center gap-3" data-testid="backfill-pending-banner">
            <History className="h-5 w-5 shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Recuperare în curs: {cameraName(config.backfillCameraId)}</p>
              <p>Serverul aduce din cloud DOAR filmele acestei camere (30 zile), apoi revine automat la sincronizarea normală.</p>
            </div>
            <Button size="sm" variant="outline" className="bg-background" onClick={() => cancelBackfill.mutate()} disabled={cancelBackfill.isPending} data-testid="cancel-backfill-btn">Anulează</Button>
          </div>
        )}

        {/* MAPPINGS */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <div><CardTitle className="text-base">Mapări camere → Reolink</CardTitle><CardDescription>Fiecare cameră: cont Reolink + UID + subfolder NAS.</CardDescription></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="bg-background" onClick={() => { if (confirm("Restaurez toate camerele (6 șantiere, 18 camere) din istoric? Camerele/proiectele existente nu se dublează.")) repairCameras.mutate(); }} disabled={repairCameras.isPending} data-testid="repair-cameras-btn">{repairCameras.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <History className="h-4 w-4 mr-1" />}Restaurează camerele</Button>
              <Button size="sm" variant="outline" className="bg-background" onClick={() => autoMap.mutate()} disabled={autoMap.isPending} data-testid="auto-map-btn">{autoMap.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Cpu className="h-4 w-4 mr-1" />}Descoperă UID-uri automat</Button>
              <MappingDialog cameras={cameras} accounts={accounts} onCreated={() => qc.invalidateQueries({ queryKey: ["cs-mappings"] })} />
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {!mappings?.length ? <p className="text-sm text-muted-foreground py-4">Nicio mapare. Adăugați o mapare cameră → cont + UID Reolink.</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Cameră</TableHead><TableHead>Cont</TableHead><TableHead>Reolink UID</TableHead><TableHead>Subfolder NAS</TableHead><TableHead>Activ</TableHead><TableHead>Ultima sincr.</TableHead><TableHead className="text-right">Acțiuni</TableHead></TableRow></TableHeader>
                <TableBody>
                  {mappings.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{cameraName(m.cameraId)}</TableCell>
                      <TableCell className="text-xs">{accountLabel(m.reolinkAccountId)}</TableCell>
                      <TableCell className="font-mono text-xs">{m.reolinkUid === "PENDING_DISCOVERY" ? <Badge variant="secondary" className="text-[10px]">de setat</Badge> : m.reolinkUid}</TableCell>
                      <TableCell className="text-xs">{m.nasSubfolder || "—"}</TableCell>
                      <TableCell><Switch checked={m.syncEnabled} onCheckedChange={(v) => toggleMapping.mutate({ id: m.id, syncEnabled: v })} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{m.lastSyncAt ? new Date(m.lastSyncAt).toLocaleString("ro-RO") : "—"}</TableCell>
                      <TableCell className="text-right flex justify-end gap-1">
                        {m.reolinkUid && m.reolinkUid !== "PENDING_DISCOVERY" && (
                          <Button size="sm" variant="outline" className="bg-background" title="Recuperează filmele acestei camere din cloud (30 zile)" onClick={() => { if (confirm(`Recuperezi din cloud filmele pentru ${cameraName(m.cameraId)} (ultimele 30 zile)?`)) backfillCamera.mutate({ cameraId: m.cameraId }); }} disabled={backfillCamera.isPending} data-testid={`backfill-camera-${m.cameraId}`}><History className="h-3.5 w-3.5" /></Button>
                        )}
                        <MappingDialog cameras={cameras} accounts={accounts} mapping={m} onCreated={() => qc.invalidateQueries({ queryKey: ["cs-mappings"] })} />
                        <Button size="sm" variant="outline" className="bg-background text-destructive hover:text-destructive" onClick={() => { if (confirm("Ștergi maparea?")) delMapping.mutate(m.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* BULK UID */}
        <BulkUidEditor mappings={mappings} cameras={cameras} accounts={accounts} onSaved={() => qc.invalidateQueries({ queryKey: ["cs-mappings"] })} />

        {/* LOGS */}
        <Card>
          <CardHeader><CardTitle className="text-base">Jurnal sincronizări</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            {!logs?.length ? <p className="text-sm text-muted-foreground py-4">Nicio sincronizare încă. Apasă «Sincronizează acum».</p> : (
              <Table>
                <TableHeader><TableRow><TableHead>Început</TableHead><TableHead>Sursă</TableHead><TableHead>Status</TableHead><TableHead>Descărcate</TableHead><TableHead>Sărite</TableHead><TableHead>Eroare</TableHead></TableRow></TableHeader>
                <TableBody>
                  {logs.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs">{new Date(l.startedAt).toLocaleString("ro-RO")}</TableCell>
                      <TableCell className="text-xs">{l.source === "agent" ? "Agent local" : "Server"}</TableCell>
                      <TableCell><Badge variant={l.status === "success" ? "default" : l.status === "running" ? "secondary" : "destructive"}>{l.status}</Badge></TableCell>
                      <TableCell>{l.clipsDownloaded ?? 0}</TableCell>
                      <TableCell>{l.clipsSkipped ?? 0}</TableCell>
                      <TableCell className="text-xs text-destructive max-w-[240px] truncate">{l.error || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}

function BulkUidEditor({ mappings, cameras, accounts, onSaved }) {
  const [vals, setVals] = useState({});
  useEffect(() => {
    const v = {};
    (mappings || []).forEach((m) => { v[m.id] = m.reolinkUid === "PENDING_DISCOVERY" ? "" : (m.reolinkUid || ""); });
    setVals(v);
  }, [mappings]);
  const cameraName = (id) => cameras?.find((c) => c.id === id)?.name ?? `Cameră #${id}`;
  const accountLabel = (id) => accounts?.find((a) => a.id === id)?.email ?? `Cont #${id}`;
  const save = useMutation({
    mutationFn: async () => {
      const changed = (mappings || []).filter((m) => (vals[m.id] || "").trim() && vals[m.id].trim() !== m.reolinkUid);
      for (const m of changed) await api.cloudSync.updateMapping(m.id, { reolinkUid: vals[m.id].trim() });
      return changed.length;
    },
    onSuccess: (n) => { toast.success(n ? `${n} UID-uri salvate` : "Nimic de salvat"); onSaved(); },
    onError: (e) => toast.error(apiError(e)),
  });
  const pending = (mappings || []).filter((m) => m.reolinkUid === "PENDING_DISCOVERY").length;
  if (!mappings?.length) return null;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Setare rapidă UID-uri</CardTitle>
          <CardDescription>Ia UID-urile din aplicația Reolink de pe PC și completează-le pe toate aici, apoi salvează o singură dată. {pending > 0 && <span className="text-destructive font-medium">{pending} camere fără UID.</span>}</CardDescription>
        </div>
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="save-all-uids-btn">{save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Salvează toate UID-urile</Button>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {mappings.map((m) => (
          <div key={m.id} className="flex items-center gap-2 rounded border p-2">
            <div className="w-28 shrink-0">
              <p className="text-sm font-medium truncate">{cameraName(m.cameraId)}</p>
              <p className="text-[10px] text-muted-foreground truncate">{accountLabel(m.reolinkAccountId)}</p>
            </div>
            <Input value={vals[m.id] ?? ""} onChange={(e) => setVals((v) => ({ ...v, [m.id]: e.target.value }))}
              placeholder="UID Reolink (ex: 95270008M5SB18D2)" className="font-mono text-xs" data-testid={`bulk-uid-input-${m.cameraId}`} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AccountDialog({ account, onSaved }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(account?.email || "");
  const [password, setPassword] = useState("");
  const [syncEnabled, setSyncEnabled] = useState(account?.syncEnabled ?? true);
  const isEdit = !!account;

  const save = useMutation({
    mutationFn: () => {
      const body = { email, syncEnabled };
      if (password) body.password = password;
      return isEdit ? api.cloudSync.updateAccount(account.id, body) : api.cloudSync.createAccount(body);
    },
    onSuccess: () => { toast.success(isEdit ? "Cont actualizat" : "Cont adăugat. Apasă «Autentifică» pentru codul 2FA."); onSaved(); setOpen(false); setPassword(""); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <>
      {isEdit ? (
        <Button size="sm" variant="outline" className="bg-background" onClick={() => { setEmail(account.email || ""); setPassword(""); setSyncEnabled(account.syncEnabled ?? true); setOpen(true); }} data-testid={`edit-account-${account.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      ) : (
        <Button size="sm" onClick={() => setOpen(true)} data-testid="add-account-btn"><Plus className="h-4 w-4 mr-1" />Cont Reolink</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isEdit ? `Editare cont: ${account.email}` : "Adaugă cont Reolink"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label>Email cont Reolink</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cloud1@highbergsolution.ro" data-testid="account-email-input" /></div>
            <div className="grid gap-1.5"><Label>Parolă {isEdit && <span className="text-xs text-muted-foreground">(gol = păstrează parola)</span>}</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="account-password-input" /></div>
            <div className="flex items-center justify-between rounded border p-2"><span className="text-sm">Cont activ pentru sincronizare</span><Switch checked={syncEnabled} onCheckedChange={setSyncEnabled} /></div>
            <p className="text-xs text-muted-foreground">Parola e stocată criptat și folosită doar pentru autentificarea automată la Reolink.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={() => { if (!email) { toast.error("Emailul e obligatoriu"); return; } if (!isEdit && !password) { toast.error("Parola e obligatorie"); return; } save.mutate(); }} disabled={save.isPending} data-testid="save-account-btn">Salvează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function LoginDialog({ account, onDone }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("idle"); // idle | code
  const [code, setCode] = useState("");
  const [email, setEmail] = useState(account.email);

  const start = useMutation({
    mutationFn: () => api.cloudSync.loginAccount(account.id),
    onSuccess: (r) => {
      onDone();
      if (r.mfaRequired) { setEmail(r.email || account.email); setStep("code"); toast.info(r.message); }
      else { toast.success(r.message || "Autentificat"); setOpen(false); }
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const resend = useMutation({
    mutationFn: () => api.cloudSync.resendAccount(account.id),
    onSuccess: () => toast.success("Cod retrimis pe email"),
    onError: (e) => toast.error(apiError(e)),
  });
  const verify = useMutation({
    mutationFn: () => api.cloudSync.verifyAccount(account.id, code.trim()),
    onSuccess: (r) => { toast.success(r.message || "Autentificat"); onDone(); setOpen(false); setStep("idle"); setCode(""); },
    onError: (e) => toast.error(apiError(e)),
  });

  const openAndStart = () => { setOpen(true); setStep("idle"); setCode(""); start.mutate(); };

  return (
    <>
      <Button size="sm" className="gap-1" onClick={openAndStart} data-testid={`login-account-${account.id}`}>
        <KeyRound className="h-3.5 w-3.5" />{account.authenticated ? "Reautentifică" : "Autentifică"}
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setStep("idle"); setCode(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" />Autentificare {account.email}</DialogTitle></DialogHeader>
          {step === "idle" ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p>Se contactează Reolink…</p>
            </div>
          ) : (
            <div className="grid gap-3">
              <p className="text-sm text-muted-foreground">Am trimis un cod de verificare pe <strong>{email}</strong>. Introdu-l mai jos (expiră în ~15 min).</p>
              <div className="grid gap-1.5"><Label>Cod de verificare (8 cifre)</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="74310805" maxLength={8} inputMode="numeric" className="font-mono text-lg tracking-widest text-center" data-testid="mfa-code-input" autoFocus />
              </div>
              <button type="button" className="text-xs text-primary underline justify-self-start disabled:opacity-50" onClick={() => resend.mutate()} disabled={resend.isPending} data-testid="resend-code-btn">{resend.isPending ? "Se retrimite…" : "Nu ai primit codul? Retrimite"}</button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); setStep("idle"); setCode(""); }}>Închide</Button>
            {step === "code" && <Button onClick={() => { if (code.trim().length < 4) { toast.error("Introdu codul"); return; } verify.mutate(); }} disabled={verify.isPending} data-testid="verify-code-btn">{verify.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}Verifică</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MappingDialog({ cameras, accounts, mapping, onCreated }) {
  const [open, setOpen] = useState(false);
  const isEdit = !!mapping;
  const [cameraId, setCameraId] = useState(mapping ? String(mapping.cameraId) : "");
  const [accountId, setAccountId] = useState(mapping ? String(mapping.reolinkAccountId) : "1");
  const [reolinkUid, setReolinkUid] = useState(mapping && mapping.reolinkUid !== "PENDING_DISCOVERY" ? mapping.reolinkUid : "");
  const [nasSubfolder, setNasSubfolder] = useState(mapping?.nasSubfolder || "");

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) return api.cloudSync.updateMapping(mapping.id, { reolinkUid, nasSubfolder: nasSubfolder || null, reolinkAccountId: Number(accountId) });
      const cam = cameras?.find((c) => c.id === Number(cameraId));
      return api.cloudSync.createMapping({ cameraId: Number(cameraId), projectId: cam?.projectId ?? null, reolinkAccountId: Number(accountId), reolinkUid, nasSubfolder: nasSubfolder || null, syncEnabled: true });
    },
    onSuccess: () => { toast.success(isEdit ? "Mapare actualizată" : "Mapare adăugată"); onCreated(); setOpen(false); if (!isEdit) { setCameraId(""); setReolinkUid(""); setNasSubfolder(""); } },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <>
      {isEdit ? (
        <Button size="sm" variant="outline" className="bg-background" onClick={() => setOpen(true)} data-testid={`edit-mapping-${mapping.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
      ) : (
        <Button size="sm" onClick={() => setOpen(true)} data-testid="add-mapping-btn"><Plus className="h-4 w-4 mr-1" />Mapare nouă</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{isEdit ? "Editare mapare" : "Mapare cameră → Reolink"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5"><Label>Cameră</Label>
              <Select value={cameraId} onValueChange={setCameraId} disabled={isEdit}>
                <SelectTrigger><SelectValue placeholder="Selectați camera" /></SelectTrigger>
                <SelectContent>{cameras?.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><Label>Cont Reolink</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{accounts?.map((a) => <SelectItem key={a.id} value={String(a.id)}>{a.email}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><Label>Reolink UID</Label><Input value={reolinkUid} onChange={(e) => setReolinkUid(e.target.value)} placeholder="ex: 95270008M5SB18D2" /></div>
            <div className="grid gap-1.5"><Label>Subfolder NAS (opțional)</Label><Input value={nasSubfolder} onChange={(e) => setNasSubfolder(e.target.value)} placeholder="Camera11" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={() => { if (!cameraId || !reolinkUid) { toast.error("Completați camera și UID-ul"); return; } save.mutate(); }} disabled={save.isPending}>Salvează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
